import { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, isNull } from 'lodash';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import TextInput from '../../components/formComponents/inputs/TextInput';
import VerbButton from '../../components/VerbButton';
import SleekLoadingIndicator from '../../components/SleekLoadingIndicator';

// eslint-disable-next-line import/named
import {
    postUserResponse,
    putUserResponse,
    putUserResponseStart,
    putWorkspaceAssetOnFinal,
    postSegmentEvent,
    getUserResponse
} from '../../actions';
import { USER_RESPONSE_TYPES, USER_RESPONSE_PARENT_TYPES, SEGMENT_EVENTS } from '../../Constants';
import { RuntimeContext } from '../../../../common/RuntimeContext';

const AnswerButton = ({ type, answer, onAnswer }) => (
    <div className={`answer-button ${answer === type ? 'selected' : 'not-selected'} mx-3`} onClick={() => onAnswer(type)}>
        <i className={`far fa-thumbs-${type === 'yes' ? 'up' : 'down'} fa-3x`}></i>
    </div>
);

AnswerButton.propTypes = {
    type: PropTypes.string,
    answer: PropTypes.string,
    onAnswer: PropTypes.func
};

const ContentRelevancyDisplay = ({ parentId }) => {
    const config = useContext(RuntimeContext);

    const [firstQuestionAnswered, setFirstQuestionAnswered] = useState(false);
    const [responseText, setResponseText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [answer, setAnswer] = useState(null);
    const [checkMark, setCheckMark] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [userResponseId, setUserResponseId] = useState(undefined);
    const [revision, setRevision] = useState(undefined);
    const [isError, setIsError] = useState(false);

    const dispatch = useDispatch();

    const userSelector = useSelector((state) => state.auth.loggedInUser);
    const userCompanySelector = useSelector((state) => state.companies);
    const userResponseSelector = useSelector((state) => state.userResponses);
    const userActivitySelector = useSelector((state) => state.userLearningAssets);
    const workspaceSelector = useSelector((state) => state.workspace);
    const userCollectionSelector = useSelector((state) => state.userCollections);

    const currentCollectionId = userSelector?.currentUserCollection?.currentCollectionId || '';
    const currentUserCollectionId = userSelector?.currentUserCollection?.id || '';

    const { userLearningAssetId: userActivityId } = useParams();
    const currentActivity = workspaceSelector?.currentUserLearningAsset || {};
    const completedSections = workspaceSelector?.byTrack[userActivityId] || {};

    const characterLimit = 800;
    const rows = Math.ceil(characterLimit / 200) || 3;
    const errorMessage = 'An error has occurred. Please try refreshing the page and resubmit your rating.'

    useEffect(async () => {
        if (userResponseSelector?.byParentId[parentId]) {
            const { userResponseText = '', userRelevantAnswer = null } = userResponseSelector?.byParentId[parentId];
            setResponseText(userResponseText);
            setFirstQuestionAnswered(!isNull(userRelevantAnswer));
            setAnswer(userRelevantAnswer ? 'yes' : 'no');
        }

        if (userResponseId) {
            const params = {
                _id: userResponseId,
                pathsContext: config.pathsWeb
            }
            const userResponse = await getUserResponse(params);
            setRevision(userResponse.revision);
        }
    }, [parentId]);

    if (Object.values(userResponseSelector?.byParentId).length && !userResponseId) {
        const userResponseForParentId = Object.values(userResponseSelector?.byParentId).find(response => {
            const responseParentId = Object.keys(response)[0];
            if(responseParentId === parentId) {
                return response
            }
        });

        const userResponse = userResponseForParentId[parentId];
        setUserResponseId(userResponse._id)
        setRevision(userResponse.revision)
    }

    const buildUserResponse = ({ type }) => ({
        body: [
            {
                _id: userResponseId,
                parentId,
                parentType: USER_RESPONSE_PARENT_TYPES.ACTIVITY_WRAP_UP,
                type: USER_RESPONSE_TYPES.CONTENT_RELEVANCY,
                userProfileId: userSelector?._id || '',
                userCompanyName: userCompanySelector?.byId[userSelector.companyId]?.name,
                userCompanyId: userSelector?.companyId || '',
                userEmail: userSelector?.email || '',
                userResponseText: responseText,
                userRelevantAnswer: type === 'yes',
                activityId: userActivitySelector?.byId[userActivityId]?.learningAssetId || null,
                activityName: userActivitySelector?.byId[userActivityId]?.trackName || null,
                collectionId: currentCollectionId,
                collectionName: userCollectionSelector?.byId[currentUserCollectionId]?.name || '',
                revision,
            }
        ],
        pathsContext: config.pathsWeb
    });

    const completeActivity = () => {
        if (
            userSelector._id !== currentActivity.userProfileId ||
            isEmpty(currentActivity) ||
            isEmpty(completedSections)
        ) {
            return Promise.resolve();
        }
        const newTrack = {
            ...currentActivity
        };

        newTrack.completedSectionItems = {
            ...newTrack.completedSectionItems,
            ...completedSections
        };

        const params = {
            userLearningAssetId: newTrack._id,
            body: newTrack,
            pathsContext: config.pathsWeb
        };
        return dispatch(putWorkspaceAssetOnFinal(params));
    };
    const handleAnswer = (type) => {
        if (((parentId && !firstQuestionAnswered) || answer !== type) && !submitted && !isSubmitting) {
            setAnswer(type);
            setFirstQuestionAnswered(true);
            if (answer && answer !== type) {
                // if user changes initial answer, update current answer
                return dispatch(putUserResponse(buildUserResponse({ type }))).then(body => {
                    setUserResponseId(body._id);
                    setRevision(body.revision);
                    return body
                }).catch(e => 
                    setIsError(true)
                );
            }
            return dispatch(postUserResponse(buildUserResponse({ type }))).then(body => {
                    setUserResponseId(body[parentId]._id);
                    setRevision(body[parentId].revision);
                    return body
                }).catch(e => 
                setIsError(true)
            );
        }
        return null;
    };

    const submitFeedback = () => {
        if (parentId && !submitted && userResponseId) {
            // only send segment event if user completes response
            const userCompany = userCompanySelector?.byId[userSelector.companyId] || {};

            postSegmentEvent({
                pathsContext: config.pathsWeb,
                body: {
                    userCompany,
                    customFields: {
                        activityId: userActivitySelector?.byId[userActivityId]?.learningAssetId || '',
                        userId: userSelector?._id,
                        companyId: userCompany._id || ''
                    },
                    eventTitle: SEGMENT_EVENTS.CONTENT_RELEVANCY_ANSWERED
                }
            });

            setIsSubmitting(true);
            dispatch(putUserResponse(buildUserResponse({ type: answer }))).then(() => {
                setCheckMark(true);
                setIsSubmitting(false);
                setSubmitted(true);
            }).catch(e => 
                setIsError(true)
            );;
        }
    };

    const saveResponseTextToState = () => {
        dispatch(putUserResponseStart(buildUserResponse({ type: answer })));
    };

    const onChangeResponseText = (e) => {
        const {
            target: { value }
        } = e;
        setResponseText(value);
    };

    const hasSubmittedClass = isSubmitting || submitted ? 'is-submitting' : 'is-not-submitting';
    return (
        <div className={`col-12 offset-2 col-md-12 content-relevancy`}>
            <h3 className={hasSubmittedClass}>Paths is curious...was this content relevant to you?</h3>
            <div className={`answer-container ${hasSubmittedClass}`}>
                <AnswerButton type={'yes'} answer={answer} onAnswer={handleAnswer} />
                <AnswerButton type={'no'} answer={answer} onAnswer={handleAnswer} />
            </div>
            {firstQuestionAnswered && (
                <div className={'follow-up-container'}>
                    <p className={`follow-up-question ${hasSubmittedClass}`}>Thanks. We'd love to know more, if you want to elaborate.</p>
                    <div className={hasSubmittedClass}>
                        <TextInput
                            componentClass={'textarea'}
                            rows={rows}
                            type={'text'}
                            disabled={submitted || isSubmitting}
                            value={responseText}
                            placeholder={''}
                            onChange={onChangeResponseText}
                            maxLength={`${characterLimit}`}
                            onBlur={saveResponseTextToState}
                        />
                    </div>
                    {isError && (
                        <p className='error'>{errorMessage}</p>
                    )}
                    {isSubmitting ? (
                        <div className={'col-5 col-offset-1 submit-button loading-indicator'}>
                            <SleekLoadingIndicator />
                        </div>
                    ) : (
                        <VerbButton
                            onClick={submitFeedback}
                            className={'col-5 col-offset-1 submit-button'}
                            disabled={!responseText.length}
                        >
                            {checkMark || submitted ? <div className={'check-mark'}></div> : 'SUBMIT'}
                        </VerbButton>
                    )}
                </div>
            )}
        </div>
    );
};

ContentRelevancyDisplay.propTypes = {
    parentId: PropTypes.string
};

export default ContentRelevancyDisplay;
